import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Row,
  Col,
  Alert,
  Progress,
  Spinner
} from 'reactstrap';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import JSZip from 'jszip';
import Swal from 'sweetalert2';
import { fetchSubjects, fetchUnits } from '../../redux/QuestionBank/SubjectsUnitsAction';
import { fetchPassages, createPassage } from '../../redux/QuestionBank/PassageAction';
import { generateBatchId, uploadBatchFile, saveUploadLog } from '../../redux/QuestionBank/UploadLogsAction';
import { textToDraftRaw, textToDraftRawWithInlineImages } from '../../util/draftToHtml';
import { uploadImage } from '../../util/imageUpload';
import { optimizeImage } from '../../util/imageOptimize';
import BulkUploadPassagesReviewModal from './BulkUploadPassagesReviewModal';

const SUPPORTED_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'];

const BulkUploadPassages = ({ userEmail }) => {
  const dispatch = useDispatch();
  const { subjects } = useSelector(state => state.questionBankSubjects);
  const { units } = useSelector(state => state.questionBankUnits);
  const { passages: existingPassages } = useSelector(state => state.questionBankPassages);

  const [file, setFile] = useState(null);
  const [parsedPassages, setParsedPassages] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);

  const [imageFiles, setImageFiles] = useState({});
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  const [isUploadingImages, setIsUploadingImages] = useState(false);

  useEffect(() => {
    dispatch(fetchSubjects());
    dispatch(fetchUnits());
    dispatch(fetchPassages());
  }, [dispatch]);

  const templateColumns = [
    'Subject Name',
    'Unit Name',
    'Passage Title',
    'Passage Text',
    'Tags (comma-separated, optional)'
  ];

  const sanitizeNameForExcel = (name) => {
    return 'R_' + name
      .replace(/[^a-zA-Z0-9]/g, '_')
      .replace(/^[0-9]/, 'N$&')
      .substring(0, 250);
  };

  const downloadTemplate = async () => {
    if (!subjects || subjects.length === 0) {
      Swal.fire('No Data', 'Please wait for subjects to load before downloading the template.', 'warning');
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Question Bank';
      workbook.created = new Date();

      const subjectsList = subjects.map(s => ({
        name: s.name,
        code: s.code || '',
        displayName: s.code ? `${s.code} ${s.name}` : s.name
      })).filter(s => s.name).sort((a, b) => a.name.localeCompare(b.name));
      const subjectDisplayNames = subjectsList.map(s => s.displayName);

      const unitsList = (units || []).map(u => {
        const subject = subjects.find(s => s.subjectId === u.subjectId);
        return {
          name: u.name,
          code: u.code || '',
          displayName: u.code ? `${u.code} ${u.name}` : u.name,
          subjectId: u.subjectId,
          subjectName: subject?.name || '',
          subjectDisplayName: subject?.code ? `${subject.code} ${subject.name}` : (subject?.name || '')
        };
      }).filter(u => u.name);

      const unitsBySubject = {};
      unitsList.forEach(u => {
        if (!unitsBySubject[u.subjectDisplayName]) {
          unitsBySubject[u.subjectDisplayName] = [];
        }
        unitsBySubject[u.subjectDisplayName].push(u.displayName);
      });

      // Lists Sheet
      const listsSheet = workbook.addWorksheet('Lists');
      listsSheet.state = 'veryHidden';

      let currentCol = 1;
      subjectDisplayNames.forEach((displayName, i) => {
        listsSheet.getCell(i + 1, currentCol).value = displayName;
      });
      workbook.definedNames.add(`Lists!$A$1:$A$${subjectDisplayNames.length}`, 'SubjectsList');

      currentCol = 2;
      Object.keys(unitsBySubject).sort().forEach(subjectDisplayName => {
        const unitsForSubject = unitsBySubject[subjectDisplayName];
        unitsForSubject.forEach((unitDisplayName, i) => {
          listsSheet.getCell(i + 1, currentCol).value = unitDisplayName;
        });
        const rangeName = sanitizeNameForExcel(subjectDisplayName);
        const colLetter = String.fromCharCode(64 + currentCol);
        workbook.definedNames.add(`Lists!$${colLetter}$1:$${colLetter}$${unitsForSubject.length}`, rangeName);
        currentCol++;
      });

      // Reference Sheet
      const refSheet = workbook.addWorksheet('Reference_Data');
      refSheet.getRow(1).values = ['Subject', 'Subject Code', 'Unit', 'Unit Code'];
      refSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      refSheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };

      const sortedUnits = [...unitsList].sort((a, b) => a.subjectName.localeCompare(b.subjectName));
      const maxRows = Math.max(subjectsList.length, sortedUnits.length);
      for (let i = 0; i < maxRows; i++) {
        refSheet.getRow(i + 2).values = [
          subjectsList[i]?.name || '', subjectsList[i]?.code || '',
          sortedUnits[i]?.name || '', sortedUnits[i]?.code || ''
        ];
      }
      refSheet.columns = [{ width: 25 }, { width: 20 }, { width: 30 }, { width: 20 }];

      // Passages Template Sheet
      const passagesSheet = workbook.addWorksheet('Passages Template');
      passagesSheet.getRow(1).values = templateColumns;
      passagesSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      passagesSheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
      passagesSheet.columns = [
        { width: 22 }, { width: 28 }, { width: 30 }, { width: 80 }, { width: 25 }
      ];

      for (let i = 2; i <= 11; i++) {
        passagesSheet.getRow(i).getCell(1).value = subjectDisplayNames[0] || '';
      }

      for (let rowNum = 2; rowNum <= 51; rowNum++) {
        passagesSheet.getCell(`A${rowNum}`).dataValidation = {
          type: 'list', allowBlank: false, formulae: ['SubjectsList'],
          showErrorMessage: true, errorTitle: 'Invalid Subject',
          error: 'Please select a subject from the dropdown list'
        };
        passagesSheet.getCell(`B${rowNum}`).dataValidation = {
          type: 'list', allowBlank: false,
          formulae: [`INDIRECT("R_"&SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(A${rowNum}," ","_"),"-","_"),"/","_"),"(","_"),")","_"),"&","_"),".","_"),",","_"))`],
          showErrorMessage: true, errorTitle: 'Invalid Unit',
          error: 'Please select a unit from the dropdown. Make sure to select a Subject first!'
        };
      }

      // Instructions Sheet
      const instructionsSheet = workbook.addWorksheet('Instructions');
      const instructions = [
        ['Bulk Upload Passages - Instructions'],
        [''],
        ['HOW TO USE THIS TEMPLATE:'],
        [''],
        ['1. Go to the "Passages Template" sheet to enter your passages'],
        ['2. Select a SUBJECT first - this will filter the Unit dropdown!'],
        ['3. Select a UNIT for the passage'],
        ['4. Enter a unique Passage Title'],
        ['5. Enter the Passage Text (supports inline image placeholders - see below)'],
        [''],
        ['CASCADING DROPDOWNS:'],
        ['✓ Subject dropdown shows all available subjects'],
        ['✓ Unit dropdown automatically shows only units for the selected subject'],
        [''],
        ['INLINE IMAGE SUPPORT (Optional):'],
        ['To embed images within passage text, use placeholders:'],
        [''],
        ['  [IMG:filename.ext]        — inserts image with default width (350px)'],
        ['  [IMG:filename.ext:400]    — inserts image with custom width (400px)'],
        [''],
        ['Example passage text:'],
        ['  "The following diagram shows cellular respiration:'],
        ['  [IMG:diagram1.png]'],
        ['  As shown above, glycolysis is the first step...'],
        ['  [IMG:table1.png:500]'],
        ['  The table summarizes ATP yield from each stage."'],
        [''],
        ['To use images:'],
        ['1. Create a ZIP file containing your Excel file and an /images folder'],
        ['2. Use [IMG:filename] placeholders in the Passage Text column'],
        ['3. Place the corresponding image files in the /images folder inside the ZIP'],
        ['4. Upload the .zip file instead of the .xlsx file'],
        [''],
        ['ZIP file structure:'],
        ['  my_passages.zip'],
        ['    ├── passages.xlsx  (your Excel file)'],
        ['    └── images/'],
        ['        ├── diagram1.png'],
        ['        ├── table1.png'],
        ['        └── ...'],
        [''],
        ['Supported image formats: .png, .jpg, .jpeg, .gif, .webp, .svg'],
        [''],
        ['NOTE: First 10 rows are pre-filled with defaults. You can add more rows as needed.'],
      ];

      instructions.forEach((row, index) => {
        const excelRow = instructionsSheet.getRow(index + 1);
        excelRow.values = row;
        if (index === 0) {
          excelRow.font = { bold: true, size: 16, color: { argb: 'FF4472C4' } };
        } else if (row[0]?.startsWith('HOW TO USE') || row[0]?.startsWith('CASCADING') || row[0]?.startsWith('INLINE IMAGE')) {
          excelRow.font = { bold: true, size: 12 };
        }
      });
      instructionsSheet.getColumn(1).width = 80;

      const sheetsOrder = ['Instructions', 'Passages Template', 'Reference_Data', 'Lists'];
      workbook.eachSheet((sheet) => {
        const index = sheetsOrder.indexOf(sheet.name);
        if (index !== -1) sheet.orderNo = index;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'passage_upload_template.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);

      Swal.fire({
        icon: 'success',
        title: 'Template Downloaded!',
        html: `
          <p>The template has <strong>cascading dropdowns</strong>:</p>
          <ul style="text-align: left;">
            <li><strong>Subject</strong> → Select first (${subjectDisplayNames.length} options)</li>
            <li><strong>Unit</strong> → Auto-filters by Subject!</li>
          </ul>
          <p><strong>10 rows pre-filled</strong> with defaults. Add more as needed.</p>
          <p class="text-info"><strong>Use [IMG:filename.png] in passage text to embed images!</strong></p>
        `,
        confirmButtonText: 'Got it!'
      });
    } catch (error) {
      console.error('Error generating template:', error);
      Swal.fire('Error', 'Failed to generate template. Please try again.', 'error');
    }
  };

  const extractZip = async (zipFile) => {
    const zip = await JSZip.loadAsync(zipFile);
    let excelFile = null;
    const images = {};

    for (const [path, entry] of Object.entries(zip.files)) {
      if (entry.dir || path.startsWith('__MACOSX')) continue;
      const filename = path.split('/').pop();
      if (!filename) continue;
      const ext = filename.split('.').pop().toLowerCase();

      if (['xlsx', 'xls', 'csv'].includes(ext) && !excelFile) {
        const blob = await entry.async('blob');
        excelFile = new File([blob], filename, { type: blob.type });
      } else if (SUPPORTED_IMAGE_EXTENSIONS.includes(ext)) {
        const blob = await entry.async('blob');
        images[filename.toLowerCase()] = new File([blob], filename, { type: `image/${ext === 'jpg' ? 'jpeg' : ext}` });
      }
    }

    if (!excelFile) throw new Error('No Excel file found in ZIP. Please include an .xlsx, .xls, or .csv file.');
    return { excelFile, images };
  };

  const parseFile = useCallback((file, zipImages = {}) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        let sheetName = workbook.SheetNames.find(name =>
          name.toLowerCase().includes('passage') || name.toLowerCase().includes('template')
        );
        if (!sheetName) {
          sheetName = workbook.SheetNames.find(name =>
            !['instructions', 'reference_data', 'lists'].includes(name.toLowerCase())
          ) || workbook.SheetNames[0];
        }

        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
          setParseErrors(['File is empty or contains only headers']);
          return;
        }

        const rows = jsonData.slice(1).filter(row => row.some(cell => cell !== undefined && cell !== ''));
        const errors = [];
        const passages = [];

        const existingTitles = new Set(
          (existingPassages || []).map(p => p.passageTitle?.toLowerCase().trim()).filter(Boolean)
        );
        const parsedTitles = new Set();

        const blobUrlMap = {};
        Object.entries(zipImages).forEach(([filename, imgFile]) => {
          blobUrlMap[filename] = URL.createObjectURL(imgFile);
        });

        rows.forEach((row, rowIndex) => {
          const rowNum = rowIndex + 2;
          const subjectRaw = row[0]?.toString().trim() || '';
          const unitRaw = row[1]?.toString().trim() || '';
          const passageTitle = row[2]?.toString().trim() || '';
          const passageTextRaw = row[3]?.toString().trim() || '';
          const tags = row[4]?.toString().trim() || '';

          if (!passageTitle && !passageTextRaw) return;

          const rowErrors = [];
          if (!subjectRaw) rowErrors.push('Subject Name is required');
          if (!unitRaw) rowErrors.push('Unit Name is required');
          if (!passageTitle) rowErrors.push('Passage Title is required');
          if (!passageTextRaw) rowErrors.push('Passage Text is required');

          if (passageTitle) {
            const titleLower = passageTitle.toLowerCase().trim();
            if (parsedTitles.has(titleLower)) {
              rowErrors.push(`Duplicate passage title "${passageTitle}" found in this upload`);
            } else if (existingTitles.has(titleLower)) {
              rowErrors.push(`Passage title "${passageTitle}" already exists in the database`);
            }
            parsedTitles.add(titleLower);
          }

          const imgMatches = passageTextRaw.match(/\[IMG:([^\]]+)\]/g) || [];
          if (imgMatches.length > 0 && Object.keys(zipImages).length === 0) {
            rowErrors.push('Image placeholders found but no ZIP file provided. Upload a ZIP with an /images folder to include images.');
          }
          imgMatches.forEach(match => {
            const parts = match.slice(5, -1).split(':');
            const filename = parts[0].trim().toLowerCase();
            if (Object.keys(zipImages).length > 0 && !zipImages[filename]) {
              rowErrors.push(`Image "${parts[0].trim()}" not found in ZIP images folder`);
            }
          });

          const matchedSubject = subjects.find(s => {
            const sLower = subjectRaw.toLowerCase();
            const nameLower = s.name?.toLowerCase() || '';
            const displayName = s.code ? `${s.code} ${s.name}`.toLowerCase() : nameLower;
            return nameLower === sLower || displayName === sLower;
          });

          let matchedUnit = units?.find(u => {
            const uLower = unitRaw.toLowerCase();
            const nameLower = u.name?.toLowerCase() || '';
            const displayName = u.code ? `${u.code} ${u.name}`.toLowerCase() : nameLower;
            return (nameLower === uLower || displayName === uLower) &&
                   matchedSubject && u.subjectId === matchedSubject?.subjectId;
          });

          const unitExistsElsewhere = !matchedUnit && units?.find(u => {
            const uLower = unitRaw.toLowerCase();
            const nameLower = u.name?.toLowerCase() || '';
            const displayName = u.code ? `${u.code} ${u.name}`.toLowerCase() : nameLower;
            return nameLower === uLower || displayName === uLower;
          });

          if (unitExistsElsewhere && matchedSubject) {
            rowErrors.push(`Unit "${unitRaw}" does not belong to Subject "${subjectRaw}". Please fix in the review.`);
          }

          if (rowErrors.length > 0) {
            errors.push({ row: rowNum, errors: rowErrors });
          }

          let passageText;
          const hasInlineImages = /\[IMG:[^\]]+\]/.test(passageTextRaw);
          if (hasInlineImages && Object.keys(blobUrlMap).length > 0) {
            passageText = textToDraftRawWithInlineImages(passageTextRaw, blobUrlMap);
          } else {
            passageText = textToDraftRaw(passageTextRaw);
          }

          passages.push({
            rowNumber: rowNum,
            subjectName: subjectRaw,
            unitName: unitRaw,
            subjectId: matchedSubject?.subjectId || null,
            unitId: matchedUnit?.unitId || null,
            passageTitle,
            passageText,
            passageTextRaw,
            tags: tags ? tags.split(',').map(t => t.trim()).filter(t => t) : [],
            hasValidationErrors: rowErrors.length > 0,
            validationErrors: rowErrors
          });
        });

        setParsedPassages(passages);
        setParseErrors(errors);
        if (passages.length > 0) setIsReviewModalOpen(true);
      } catch (error) {
        console.error('Error parsing file:', error);
        setParseErrors([`Error parsing file: ${error.message}`]);
      }
    };

    reader.readAsArrayBuffer(file);
  }, [subjects, units, existingPassages]);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls', 'csv', 'zip'].includes(fileExtension)) {
        Swal.fire('Invalid File', 'Please upload an Excel (.xlsx, .xls), CSV, or ZIP file.', 'error');
        return;
      }
      setFile(selectedFile);
      setParsedPassages([]);
      setParseErrors([]);
      setUploadResults(null);

      if (fileExtension === 'zip') {
        try {
          const { excelFile, images } = await extractZip(selectedFile);
          setImageFiles(images);
          parseFile(excelFile, images);
        } catch (err) {
          setParseErrors([err.message]);
        }
      } else {
        setImageFiles({});
        parseFile(selectedFile);
      }
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const fileExtension = droppedFile.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls', 'csv', 'zip'].includes(fileExtension)) {
        Swal.fire('Invalid File', 'Please upload an Excel (.xlsx, .xls), CSV, or ZIP file.', 'error');
        return;
      }
      setFile(droppedFile);
      setParsedPassages([]);
      setParseErrors([]);
      setUploadResults(null);

      if (fileExtension === 'zip') {
        try {
          const { excelFile, images } = await extractZip(droppedFile);
          setImageFiles(images);
          parseFile(excelFile, images);
        } catch (err) {
          setParseErrors([err.message]);
        }
      } else {
        setImageFiles({});
        parseFile(droppedFile);
      }
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); };

  const replaceBlobUrls = (raw, blobToFirebaseMap) => {
    if (!raw || !raw.entityMap) return raw;
    const result = JSON.parse(JSON.stringify(raw));
    Object.values(result.entityMap).forEach(entity => {
      if (entity.type === 'IMAGE' && entity.data?.src) {
        const firebaseUrl = blobToFirebaseMap[entity.data.src];
        if (firebaseUrl) entity.data.src = firebaseUrl;
      }
    });
    return result;
  };

  const handleSavePassages = async (passagesToSave) => {
    setIsUploading(true);
    setUploadProgress(0);
    setIsReviewModalOpen(false);

    const uploadedImageUrls = {};
    const imageEntries = Object.entries(imageFiles);
    if (imageEntries.length > 0) {
      setIsUploadingImages(true);
      setImageUploadProgress(0);
      for (let i = 0; i < imageEntries.length; i++) {
        const [filename, imgFile] = imageEntries[i];
        try {
          const optimized = await optimizeImage(imgFile);
          const url = await uploadImage(optimized, `bulk_upload_passages/images/${Date.now()}_${imgFile.name}`);
          uploadedImageUrls[filename] = url;
        } catch (err) {
          console.error(`Failed to upload image ${filename}:`, err);
        }
        setImageUploadProgress(Math.round(((i + 1) / imageEntries.length) * 100));
      }
      setIsUploadingImages(false);
    }

    const buildEntityBlobMap = (passageText) => {
      if (!passageText || !passageText.entityMap) return {};
      const map = {};
      Object.values(passageText.entityMap).forEach(entity => {
        if (entity.type === 'IMAGE' && entity.data?.src && entity.data.src.startsWith('blob:')) {
          const altFilename = (entity.data.alt || '').toLowerCase();
          if (altFilename && uploadedImageUrls[altFilename]) {
            map[entity.data.src] = uploadedImageUrls[altFilename];
          }
        }
      });
      return map;
    };

    const bulkUploadId = generateBatchId();
    const results = { success: 0, failed: 0, errors: [] };
    const passageTitles = [];

    for (let i = 0; i < passagesToSave.length; i++) {
      const passage = passagesToSave[i];

      try {
        if (!passage.subjectId || !passage.unitId) {
          results.failed++;
          results.errors.push({ row: passage.rowNumber, error: 'Subject or Unit not matched.' });
          continue;
        }

        const entityBlobMap = buildEntityBlobMap(passage.passageText);
        let finalPassageText = passage.passageText;
        if (Object.keys(entityBlobMap).length > 0) {
          finalPassageText = replaceBlobUrls(passage.passageText, entityBlobMap);
        }
        if (typeof finalPassageText === 'string') {
          finalPassageText = textToDraftRaw(finalPassageText);
        }

        // Check for remaining blob URLs (images that failed to upload)
        const hasUnresolvedBlobs = finalPassageText?.entityMap &&
          Object.values(finalPassageText.entityMap).some(
            entity => entity.type === 'IMAGE' && entity.data?.src?.startsWith('blob:')
          );
        if (hasUnresolvedBlobs) {
          results.failed++;
          results.errors.push({ row: passage.rowNumber, error: 'Some images failed to upload. Please try again.' });
          continue;
        }

        const passageData = {
          subjectId: passage.subjectId,
          unitId: passage.unitId,
          passageTitle: passage.passageTitle,
          passageText: finalPassageText,
          tags: passage.tags || [],
          source: 'bulk_upload',
          bulkUploadId
        };

        const result = await dispatch(createPassage(passageData, userEmail));
        if (result.success) {
          results.success++;
          passageTitles.push(passage.passageTitle);
        } else {
          results.failed++;
          results.errors.push({ row: passage.rowNumber, error: result.error || 'Unknown error' });
        }
      } catch (error) {
        results.failed++;
        results.errors.push({ row: passage.rowNumber, error: error.message });
      }

      setUploadProgress(Math.round(((i + 1) / passagesToSave.length) * 100));
    }

    const { storagePath, downloadUrl } = file
      ? await uploadBatchFile(file, 'bulk_upload_passages', bulkUploadId)
      : { storagePath: null, downloadUrl: null };
    await dispatch(saveUploadLog('bulk_upload_passages', {
      batchId: bulkUploadId,
      fileName: file?.name || 'unknown',
      storagePath, downloadUrl,
      uploadedBy: userEmail,
      totalRows: passagesToSave.length,
      successCount: results.success,
      failCount: results.failed,
      errors: results.errors.slice(0, 50),
      passageTitles,
    }));

    setIsUploading(false);
    setUploadResults(results);

    if (results.success > 0) {
      dispatch(fetchPassages());
      Swal.fire({
        icon: results.failed === 0 ? 'success' : 'warning',
        title: results.failed === 0 ? 'All Passages Uploaded!' : 'Upload Complete with Errors',
        html: `<p><strong>${results.success}</strong> passage(s) uploaded successfully.</p>
          ${results.failed > 0 ? `<p class="text-danger"><strong>${results.failed}</strong> failed.</p>` : ''}`,
        confirmButtonText: 'OK'
      });
    }
  };

  return (
    <>
      <Card className="shadow mb-4">
        <CardHeader className="border-0">
          <Row className="align-items-center">
            <Col>
              <h3 className="mb-0">
                <i className="fas fa-book-open mr-2 text-success"></i>
                Bulk Upload Passages
              </h3>
            </Col>
            <Col className="text-right">
              <Button color="primary" size="sm" onClick={downloadTemplate} disabled={!subjects || subjects.length === 0}>
                <i className="fas fa-download mr-2"></i>Download Template
              </Button>
            </Col>
          </Row>
        </CardHeader>
        <CardBody>
          <Alert color="info" className="mb-4">
            <h5 className="alert-heading">
              <i className="fas fa-info-circle mr-2"></i>How It Works
            </h5>
            <ol className="mb-0 pl-3">
              <li>Download the <strong>Excel template</strong> (has cascading Subject → Unit dropdowns)</li>
              <li>Fill in your passages — no images? Just upload the <strong>.xlsx</strong> directly</li>
              <li>To embed images inline, use <strong>[IMG:filename.png]</strong> or <strong>[IMG:filename.png:400]</strong> (custom width) in the Passage Text column</li>
              <li>To include images: create a <strong>ZIP</strong> with the Excel file + an <strong>/images</strong> folder</li>
              <li>Upload the file (Excel or ZIP), review, and save</li>
            </ol>
            <hr className="my-2" />
            <small className="d-block mt-2">
              <strong>Example passage text:</strong><br />
              <span className="d-block mt-1" style={{ whiteSpace: 'pre-wrap', background: 'rgba(255,255,255,0.15)', padding: '8px', borderRadius: '4px' }}>
                {'The following diagram shows cellular respiration:\n'}<strong>{'[IMG:diagram1.png]'}</strong>{'\nAs shown above, glycolysis is the first step...\n'}<strong>{'[IMG:table1.png:500]'}</strong>{'\nThe table summarizes ATP yield from each stage.'}
              </span>
            </small>
          </Alert>

          <div
            className="border rounded p-5 text-center bg-light"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            style={{ cursor: 'pointer', borderStyle: 'dashed' }}
            onClick={() => document.getElementById('bulk-upload-passages-input').click()}
          >
            <input
              type="file"
              id="bulk-upload-passages-input"
              accept=".xlsx,.xls,.csv,.zip"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <i className="fas fa-cloud-upload-alt fa-3x text-muted mb-3"></i>
            <h4 className="text-muted">
              {file ? file.name : 'Drop your file here or click to browse'}
            </h4>
            <p className="text-muted mb-0">Supported formats: .xlsx, .xls, .csv, .zip (with images)</p>
          </div>

          {parseErrors.length > 0 && parsedPassages.length === 0 && (
            <Alert color="danger" className="mt-3">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              {parseErrors.map((err, i) => (
                <div key={i}>{typeof err === 'string' ? err : `Row ${err.row}: ${err.errors?.join(', ')}`}</div>
              ))}
            </Alert>
          )}

          {isUploadingImages && (
            <div className="mt-4">
              <div className="d-flex align-items-center mb-2">
                <Spinner size="sm" color="warning" className="mr-2" />
                <span>Uploading images... {imageUploadProgress}%</span>
              </div>
              <Progress value={imageUploadProgress} color="warning" />
            </div>
          )}

          {isUploading && (
            <div className="mt-4">
              <div className="d-flex align-items-center mb-2">
                <Spinner size="sm" color="primary" className="mr-2" />
                <span>Uploading passages... {uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} color="primary" />
            </div>
          )}

          {uploadResults && (
            <div className="mt-4">
              <Alert color={uploadResults.failed === 0 ? 'success' : 'warning'}>
                <strong>Upload Complete:</strong> {uploadResults.success} succeeded, {uploadResults.failed} failed
              </Alert>
              {uploadResults.errors.length > 0 && (
                <div className="mt-2">
                  <h5>Errors:</h5>
                  <ul className="text-danger">
                    {uploadResults.errors.map((err, i) => (
                      <li key={i}>Row {err.row}: {err.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      <BulkUploadPassagesReviewModal
        isOpen={isReviewModalOpen}
        toggle={() => setIsReviewModalOpen(false)}
        passages={parsedPassages}
        subjects={subjects}
        units={units}
        parseErrors={parseErrors}
        onSave={handleSavePassages}
        onUpdatePassage={(index, updaterOrPassage) => {
          setParsedPassages(prev => {
            const updated = [...prev];
            updated[index] = typeof updaterOrPassage === 'function'
              ? updaterOrPassage(updated[index])
              : updaterOrPassage;
            return updated;
          });
        }}
      />
    </>
  );
};

export default BulkUploadPassages;
